/**
 * Hülle nach der Vorlage.
 *
 * Navigation, Modulschalter und Rechte kommen aus `entwurf/navigation.ts` und
 * sind dort wörtlich aus dem HTML-Prototyp übernommen. Diese Datei setzt sie
 * um — sie erfindet keine Struktur dazu.
 *
 * Routen, für die die Ansicht noch nicht nachgebaut ist, zeigen den Hinweis
 * aus der Vorlage statt einer Ersatzseite. Das hält sichtbar, was noch fehlt,
 * statt es zu verdecken.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { ChevronLeft, ChevronRight, ChevronUp, Search } from 'lucide-react';
import { msal, bereich } from './anmeldung/msal.js';
import { DEV_MANDANT, OHNE_ANMELDUNG } from './anmeldung/entwicklung.js';
import { KONFIG as ASSET_KONFIG } from './seiten/assetkonfig.js';
import { Kopfzeile } from './bausteine/Kopfzeile.js';
import { Seitenleiste } from './bausteine/Seitenleiste.js';
import { Seitenkopf } from './bausteine/Seitenkopf.js';
import { Fehlergrenze } from './bausteine/Fehlergrenze.js';
import { useModuleMelden } from './bausteine/Modulrahmen.js';
import { useStand, useStandSetzen } from './bausteine/Standrahmen.js';
import { stil } from './bausteine/stil.js';
import { datum } from './entwurf/marker.js';
import { Laedt } from './bausteine/Zustaende.js';
import { useNavigationszaehler } from './api/zaehler.js';
import { kopfFuerRoute } from './entwurf/kopfbereich.js';

/* --- Seiten werden nachgeladen, nicht vorab geliefert --------------------
 *
 * Gemessen am 19.08.2026: das Startbuendel war **836 kB** (226 kB gepackt) in
 * einem Stueck. Darin lagen alle fuenfundzwanzig Ansichten — die Heatmap, der
 * Beziehungsgraph, der Berichtsbaukasten —, auch fuer jemanden, der nur die
 * Startseite oeffnet und wieder geht.
 *
 * `lazy` schneidet je Seite ein eigenes Stueck heraus; geladen wird es beim
 * ersten Aufruf der Route. Der Preis ist ein kurzer Ladezustand beim ersten
 * Wechsel auf eine Seite — dafuer gibt es `Suspense` weiter unten, mit
 * demselben Platzhalter, den die Anwendung ohnehin fuer Ladevorgaenge nutzt.
 *
 * Die Ansichten sind **benannte** Exporte; `lazy` erwartet einen
 * Vorgabeexport. Die Umformung steht deshalb an jeder Zeile statt in einer
 * Hilfsfunktion: eine generische Hilfe muesste den Modulpfad zur Laufzeit
 * zusammensetzen, und dann findet das Buendelwerkzeug nichts mehr zum
 * Aufteilen — der Gewinn waere weg, ohne dass es jemand merkt.
 */
const Startseite = lazy(() => import('./seiten/Startseite.js').then((m) => ({ default: m.Startseite })));
const CockpitSeite = lazy(() => import('./seiten/Cockpit.js').then((m) => ({ default: m.Cockpit })));
const Rollenkatalog = lazy(() => import('./seiten/Rollenkatalog.js').then((m) => ({ default: m.Rollenkatalog })));
const Rollenmatrix = lazy(() => import('./seiten/Rollenmatrix.js').then((m) => ({ default: m.Rollenmatrix })));
const Rollendetail = lazy(() => import('./seiten/Rollendetail.js').then((m) => ({ default: m.Rollendetail })));
const Rolleneditor = lazy(() => import('./seiten/Rolleneditor.js').then((m) => ({ default: m.Rolleneditor })));
const Heatmap = lazy(() => import('./seiten/Heatmap.js').then((m) => ({ default: m.Heatmap })));
const Organisation = lazy(() => import('./seiten/Organisation.js').then((m) => ({ default: m.Organisation })));
const Assets = lazy(() => import('./seiten/Assets.js').then((m) => ({ default: m.Assets })));
const Lizenzen = lazy(() => import('./seiten/Lizenzen.js').then((m) => ({ default: m.Lizenzen })));
const Personen = lazy(() => import('./seiten/Personen.js').then((m) => ({ default: m.Personen })));
const Vertretungen = lazy(() => import('./seiten/Vertretungen.js').then((m) => ({ default: m.Vertretungen })));
const Personendetail = lazy(() => import('./seiten/Personendetail.js').then((m) => ({ default: m.Personendetail })));
const MeineAntraege = lazy(() => import('./seiten/MeineAntraege.js').then((m) => ({ default: m.MeineAntraege })));
const Genehmigungen = lazy(() => import('./seiten/Genehmigungen.js').then((m) => ({ default: m.Genehmigungen })));
const Lebenszyklus = lazy(() => import('./seiten/Lebenszyklus.js').then((m) => ({ default: m.Lebenszyklus })));
const Antragsassistent = lazy(() => import('./seiten/Antragsassistent.js').then((m) => ({ default: m.Antragsassistent })));
const Governance = lazy(() => import('./seiten/Governance.js').then((m) => ({ default: m.Governance })));
const Ausfuehrung = lazy(() => import('./seiten/Ausfuehrung.js').then((m) => ({ default: m.Ausfuehrung })));
const Abfragen = lazy(() => import('./seiten/Abfragen.js').then((m) => ({ default: m.Abfragen })));
const Querschnitt = lazy(() => import('./seiten/Querschnitt.js').then((m) => ({ default: m.Querschnitt })));
const Beziehungsgraph = lazy(() => import('./seiten/Beziehungsgraph.js').then((m) => ({ default: m.Beziehungsgraph })));
const Berechtigungsfeld = lazy(() => import('./seiten/Berechtigungsfeld.js').then((m) => ({ default: m.Berechtigungsfeld })));
const Konten = lazy(() => import('./seiten/Konten.js').then((m) => ({ default: m.Konten })));

import { useGemerkt } from './bausteine/speicher.js';
import { useIch, useListe, useUebersicht } from './api/haken.js';
import { DEMO } from './entwurf/demo.js';
import { INSTANZ_MANDANT } from './entwurf/instanz.js';
import { reiterSetzen } from './entwurf/reiter.js';
import {
  NAVIGATION, PROFILE, hatRecht, rechtFuerRoute, routeErlaubt,
  type Eintrag, type Profil,
} from './entwurf/navigation.js';

/* ------------------------------------ Anbindung der Routen an die API ---
   Nur die Ansichten, für die die Schnittstelle heute Daten liefert. Alle
   übrigen Routen der Vorlage stehen weiterhin in der Navigation und melden
   ehrlich, dass sie noch nicht nachgebaut sind. */

interface Spalte {
  feld: string;
  kopf: string;
  art?: 'text' | 'zahl' | 'marker' | 'jaNein' | 'datum';
  anteil?: boolean;
  breit?: boolean;
}

interface Anbindung {
  sammlung: string;
  spalten: Spalte[];
  /** Feste Einschränkung, z. B. eine Assetkategorie. */
  filter?: Record<string, string>;
}

const ANBINDUNG: Record<string, Anbindung> = {
  '/matrix': {
    sammlung: 'zuordnungen',
    spalten: [
      { feld: 'rolleId', kopf: 'Rolle' },
      { feld: 'asset', kopf: 'Asset', breit: true },
      { feld: 'kategorie', kopf: 'Kategorie' },
      { feld: 'berechtigungsart', kopf: 'Berechtigungsart' },
      { feld: 'gruppenName', kopf: 'Über Gruppe', breit: true },
      { feld: 'groupId', kopf: 'Group ID' },
      { feld: 'provisionierung', kopf: 'Vergabe' },
      { feld: 'status', kopf: 'Status' },
    ],
  },
  '/rollen': {
    sammlung: 'rollen',
    spalten: [
      { feld: 'rolleId', kopf: 'Rollen-ID' },
      { feld: 'name', kopf: 'Bezeichnung', breit: true },
      { feld: 'oeCode', kopf: 'OE' },
      { feld: 'rollentyp', kopf: 'Typ' },
      { feld: 'kritikalitaet', kopf: 'Kritikalität', art: 'marker' },
      { feld: 'anzahlBerechtigungen', kopf: 'Berechtigungen', art: 'zahl', anteil: true },
      { feld: 'lizenzkostenProJahr', kopf: 'IT-Lizenzkosten', art: 'zahl' },
      { feld: 'vollstaendig', kopf: 'Vollständig', art: 'jaNein' },
      { feld: 'status', kopf: 'Status' },
    ],
  },
  '/assets/gruppen': {
    sammlung: 'gruppen',
    spalten: [
      { feld: 'gruppenId', kopf: 'Gruppen-ID' },
      { feld: 'name', kopf: 'Bezeichnung', breit: true },
      { feld: 'groupId', kopf: 'Group ID' },
      { feld: 'idp', kopf: 'Verzeichnis' },
      { feld: 'gruppentyp', kopf: 'Typ' },
      { feld: 'konform', kopf: 'Konvention', art: 'jaNein' },
      { feld: 'mitgliederAnzahl', kopf: 'Mitglieder', art: 'zahl', anteil: true },
      { feld: 'verwendetInRollen', kopf: 'In Rollen', art: 'zahl' },
    ],
  },
  '/organisation': {
    sammlung: 'organisationseinheiten',
    spalten: [
      { feld: 'code', kopf: 'Kürzel' },
      { feld: 'name', kopf: 'Bezeichnung', breit: true },
      { feld: 'anzahlRollen', kopf: 'Rollen', art: 'zahl', anteil: true },
    ],
  },
  '/personen': {
    sammlung: 'personen',
    spalten: [
      { feld: 'personId', kopf: 'Kennung' },
      { feld: 'name', kopf: 'Name', breit: true },
      { feld: 'oeCode', kopf: 'OE' },
      { feld: 'standardrolle', kopf: 'Standardrolle' },
      { feld: 'beschaeftigungsart', kopf: 'Art' },
      { feld: 'eintritt', kopf: 'Eintritt', art: 'datum' },
      { feld: 'zusatzberechtigungen', kopf: 'Zusätze', art: 'zahl', anteil: true },
      { feld: 'status', kopf: 'Status' },
    ],
  },
  '/offene-punkte': {
    sammlung: 'offene-punkte',
    spalten: [
      { feld: 'punktId', kopf: 'Nummer' },
      { feld: 'oeCode', kopf: 'OE' },
      { feld: 'thema', kopf: 'Thema', breit: true },
      { feld: 'status', kopf: 'Status', art: 'marker' },
      { feld: 'verantwortlich', kopf: 'Verantwortlich' },
      { feld: 'faelligkeit', kopf: 'Fällig', art: 'datum' },
    ],
  },
};

/**
 * Routen, deren Ansicht bereits nach dem Entwurf nachgebaut ist. Sie stehen
 * einzeln weiter unten und bekommen keine allgemeine Listenansicht.
 */
const NACHGEBAUT = new Set(['/', '/cockpit', '/rollen', '/matrix', '/rollen/bearbeiten', '/heatmap', '/graph', '/organisation',
  '/assets/lizenzen', '/personen', '/vertretungen',
  '/antraege/neu', '/antraege/meine', '/genehmigungen', '/lifecycle',
  '/rezertifizierung', '/sod', '/richtlinien', '/offene-punkte', '/reports', '/abfragen',
  '/rechte', '/konten', '/aktive-nutzer', '/ausfuehrung', '/audit', '/sitemap',
  '/dev/issues', '/profil',
  ...Object.keys(ASSET_KONFIG)]);

/* ------------------------------------------------------------ Bausteine */

const zahlFormat = new Intl.NumberFormat('de-DE');
const datumFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

function Marker({ wert }: { wert: string }) {
  const a = wert.toLowerCase().replace(/\s+/g, '');
  const k = a === 'sehrhoch' ? 'sehrhoch' : a === 'hoch' ? 'hoch'
    : a === 'mittel' ? 'mittel' : a === 'erledigt' ? 'gut'
    : a === 'offen' ? 'warnung' : 'gering';
  return <span className={`marker marker--${k}`}>{wert}</span>;
}

function Zelle({ spalte, wert, basis }: { spalte: Spalte; wert: unknown; basis: number }) {
  if (wert === null || wert === undefined || wert === '') {
    return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  }
  switch (spalte.art) {
    case 'marker': return <Marker wert={String(wert)} />;
    case 'jaNein':
      return <span className={`marker marker--${wert ? 'gut' : 'warnung'}`}>{wert ? 'ja' : 'nein'}</span>;
    case 'datum': return <>{datumFormat.format(new Date(String(wert)))}</>;
    case 'zahl': {
      const z = Number(wert);
      const text = zahlFormat.format(z);
      if (!spalte.anteil || basis <= 0) return <span className="zahl">{text}</span>;
      return (
        <span className="anteil zahl">
          <span className="anteil__balken bewegt-balken"
                style={{ inlineSize: `${Math.max(0, Math.min(1, z / basis)) * 100}%` }}
                aria-hidden="true" />
          <span className="anteil__wert">{text}</span>
        </span>
      );
    }
    default: return <>{String(wert)}</>;
  }
}

/* -------------------------------------------------------- Listenansicht */

function Liste({ eintrag, anbindung, mandant }: {
  eintrag: Eintrag; anbindung: Anbindung; mandant: string;
}) {
  const [parameter, setParameter] = useSearchParams();
  const seite = Number(parameter.get('seite') ?? '1');
  const sortierung = parameter.get('sortierung') ?? undefined;
  const richtung = (parameter.get('richtung') as 'auf' | 'ab' | null) ?? undefined;
  const suche = parameter.get('suche') ?? '';

  const [entwurf, setEntwurf] = useState(suche);
  useEffect(() => { setEntwurf(suche); }, [suche]);
  useEffect(() => {
    if (entwurf === suche) return;
    const uhr = setTimeout(() => setParameter((alt) => {
      const neu = new URLSearchParams(alt);
      if (entwurf) neu.set('suche', entwurf); else neu.delete('suche');
      neu.set('seite', '1');
      return neu;
    }), 280);
    return () => clearTimeout(uhr);
  }, [entwurf, suche, setParameter]);

  const { daten, laedt, fehler, erneut } = useListe<Record<string, unknown>>(
    anbindung.sammlung, mandant,
    { seite, groesse: 50, sortierung, richtung, suche: suche || undefined,
      filter: anbindung.filter },
  );

  const sortieren = useCallback((feld: string) => setParameter((alt) => {
    const neu = new URLSearchParams(alt);
    if (neu.get('sortierung') === feld) {
      neu.set('richtung', neu.get('richtung') === 'ab' ? 'auf' : 'ab');
    } else { neu.set('sortierung', feld); neu.set('richtung', 'auf'); }
    neu.set('seite', '1');
    return neu;
  }), [setParameter]);

  const basis = useMemo(() => {
    const feld = anbindung.spalten.find((s) => s.anteil)?.feld;
    if (!feld || !daten) return 0;
    return daten.zeilen.reduce((m, z) => Math.max(m, Number(z[feld] ?? 0)), 0);
  }, [daten, anbindung.spalten]);

  return (
    <>
      <div className="seitenkopf">
        <div className="seitenkopf__text">
          <h1>{eintrag.titel}</h1>
          <p className="seitenkopf__hinweis">{eintrag.beschreibung}</p>
        </div>
      </div>

      <section className="karte">
        <div className="werkzeuge">
          <label className="nur-vorlesen" htmlFor="suche">{eintrag.titel} durchsuchen</label>
          <div style={{ position: 'relative', flex: '1 1 220px', display: 'flex' }}>
            <Search size={15} aria-hidden="true" style={{
              position: 'absolute', insetInlineStart: 10, insetBlockStart: 9,
              color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
            <input id="suche" className="suchfeld" type="search" value={entwurf}
                   onChange={(ev) => setEntwurf(ev.target.value)}
                   placeholder="Suchen — Anfang des Begriffs genügt"
                   style={{ paddingInlineStart: 30 }} autoComplete="off" spellCheck={false} />
          </div>
        </div>

        {laedt && <div className="ladebalken" role="status" aria-label="Wird geladen" />}

        {fehler && (
          <div className="fehlerfeld" role="alert">
            <p>{fehler}</p>
            <button type="button" className="knopf knopf--rand" onClick={erneut}>Erneut versuchen</button>
          </div>
        )}

        {!fehler && daten && daten.zeilen.length === 0 && (
          <div className="leer"><p>Zu dieser Einschränkung gibt es keinen Eintrag.</p></div>
        )}

        {!fehler && daten && daten.zeilen.length > 0 && (
          <div className="rollt-quer">
            <table className="tabelle">
              <caption className="nur-vorlesen">
                {eintrag.titel}, Seite {daten.seite} von {daten.seiten}, {daten.gesamt} Einträge
              </caption>
              <thead>
                <tr>
                  {anbindung.spalten.map((s) => {
                    const aktiv = sortierung === s.feld;
                    const zustand = aktiv ? (richtung === 'ab' ? 'descending' : 'ascending') : 'none';
                    return (
                      <th key={s.feld} scope="col" aria-sort={zustand}>
                        <button type="button" className="sortknopf" aria-sort={zustand}
                                onClick={() => sortieren(s.feld)}>
                          {s.kopf}
                          <ChevronUp size={13} className="sortknopf__pfeil" aria-hidden="true" />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {daten.zeilen.map((zeile, i) => (
                  <tr key={String(zeile[anbindung.spalten[0]!.feld] ?? i)}
                      className="bewegt-gestaffelt" style={{ ['--rang' as string]: i }}>
                    {anbindung.spalten.map((s) => (
                      <td key={s.feld} className={s.art === 'zahl' ? 'zahl' : undefined}
                          style={s.breit ? { whiteSpace: 'normal', minInlineSize: '18ch' } : undefined}>
                        <Zelle spalte={s} wert={zeile[s.feld]} basis={basis} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {daten && daten.gesamt > 0 && (
          <div className="blaettern">
            <span>
              {zahlFormat.format((daten.seite - 1) * daten.groesse + 1)}–
              {zahlFormat.format(Math.min(daten.seite * daten.groesse, daten.gesamt))}
              {' von '}<strong>{zahlFormat.format(daten.gesamt)}</strong>
            </span>
            <div className="blaettern__rest">
              <button type="button" className="knopf knopf--rand knopf--nur-zeichen"
                      onClick={() => setParameter((a) => { const n = new URLSearchParams(a); n.set('seite', String(daten.seite - 1)); return n; })}
                      disabled={daten.seite <= 1} aria-label="Vorherige Seite">
                <ChevronLeft size={15} aria-hidden="true" />
              </button>
              <span>Seite {daten.seite} von {daten.seiten}</span>
              <button type="button" className="knopf knopf--rand knopf--nur-zeichen"
                      onClick={() => setParameter((a) => { const n = new URLSearchParams(a); n.set('seite', String(daten.seite + 1)); return n; })}
                      disabled={daten.seite >= daten.seiten} aria-label="Nächste Seite">
                <ChevronRight size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

/* `NochNicht` ist am 27.08.2026 entfallen — ein Zweig, den keine Route
   mehr erreicht.

   Er zeigte „Diese Ansicht ist im Entwurf enthalten, aber noch nicht
   nachgebaut" und stand fuer jeden Navigationseintrag ohne eigene
   Ansicht. Am 27.08.2026 nachgezaehlt: **alle 32 Routen** der Navigation
   sind bedient — 26 ueber `NACHGEBAUT`, sechs ueber `ANBINDUNG`, die
   fuenf Assetlisten ueber `ASSET_KONFIG`.

   Ein unerreichbarer Zweig ist harmlos, solange er dasteht, und
   gefaehrlich, sobald ihn jemand wieder erreichbar macht: er antwortet
   auf eine fehlende Ansicht mit einer Beruhigung („steht bewusst in der
   Navigation"), nicht mit einem Fehler. `werkzeuge/test/routenlage.test.mjs`
   haelt jetzt fest, dass jede Route eine Ansicht hat — dann faellt eine
   neue ohne beim Pruefen auf und nicht erst beim Benutzen. */

/**
 * Antwort auf eine Route, die der Mandant nicht fuehrt.
 *
 * Links auf gesperrte Bereiche blendet die Oberflaeche aus — die Adresse bleibt
 * aber tippbar, und Lesezeichen ueberleben einen Mandantenwechsel. Darum endet
 * der Weg hier statt in einer halb geladenen Ansicht.
 */
function NichtImProfil({ profil }: { profil: Profil }) {
  return (
    <>
      <div className="seitenkopf">
        <div className="seitenkopf__text">
          <h1>Nicht Teil dieses Mandanten</h1>
          <p className="seitenkopf__hinweis">
            {profil.name} führt diesen Bereich nicht.
          </p>
        </div>
      </div>
      <section className="karte">
        <div className="leer">
          <p style={{ maxInlineSize: '52ch' }}>
            Der Funktionsumfang ist je Mandant festgelegt. Für {profil.name} ist
            dieser Bereich abgeschaltet — es werden dazu weder Daten gehalten
            noch abgefragt.
          </p>
          <p style={{ marginTop: 12 }}>
            <Link to="/" className="knopf knopf--haupt">Zur Startseite</Link>
          </p>
        </div>
      </section>
    </>
  );
}

/**
 * Kein Recht auf diese Ansicht.
 *
 * Bewusst unterschieden von „Nicht Teil dieses Mandanten": das eine ist der
 * Zuschnitt des Mandanten und gilt fuer alle, das andere haengt an der eigenen
 * Anwendungsrolle. Wer die beiden Faelle in eine Meldung wirft, laesst den
 * Nutzer raten, ob er die falsche Rolle hat oder den falschen Mandanten.
 */
/**
 * Zwei sehr verschiedene Lagen, die vorher denselben Text bekamen.
 *
 * „Diese eine Ansicht ist für Sie gesperrt" ist eine Auskunft über einen
 * Bereich. „Ihnen ist noch **gar keine** Rolle zugewiesen" ist eine über den
 * Zugang — und sie sagt der Person, dass jemand etwas tun muss, nicht sie
 * selbst. Am 14.08.2026 aufgefallen, weil beide Fälle „Ihre Anwendungsrolle
 * IAM Admin führt es nicht" meldeten: der Satz nannte eine Rolle, die das
 * Konto gar nicht trug.
 */
function KeinRecht({ recht, anwendungsrollen, ohneRolle }: {
  recht: string;
  /**
   * Die Anwendungsrollen des Kontos aus `/api/ich`.
   *
   * Hier stand die **Persona** — der Satz nannte damit eine Rolle aus dem
   * Browserspeicher. Am 14.08.2026 war schon einmal aufgefallen, dass er
   * „Ihre Anwendungsrolle IAM Admin fuehrt es nicht" meldete, waehrend das
   * Konto gar keine Rolle trug; behoben wurde damals nur der Fall „keine
   * Rolle", nicht die Quelle des Namens.
   *
   * `undefined` heisst „die Schnittstelle nennt sie nicht" — dann steht im
   * Satz kein Name statt eines erfundenen.
   */
  anwendungsrollen: readonly string[] | undefined;
  ohneRolle: boolean;
}) {
  return (
    <>
      <div className="seitenkopf">
        <div className="seitenkopf__text">
          <h1>{ohneRolle ? 'Noch keine Rolle zugewiesen' : 'Keine Berechtigung'}</h1>
          <p className="seitenkopf__hinweis">
            {ohneRolle
              ? 'Ihr Zugang ist angelegt, trägt aber noch keine Anwendungsrolle.'
              : 'Diese Ansicht setzt ein Recht voraus, das Ihre Rolle nicht trägt.'}
          </p>
        </div>
      </div>
      <section className="karte">
        <div className="leer">
          {ohneRolle ? (
            <p style={{ maxInlineSize: '52ch' }}>
              Sie sind angemeldet, und Ihr Konto steht bei der Verwaltung unter
              <strong> „Zugang freischalten"</strong>. Sobald Ihnen dort eine
              Anwendungsrolle zugewiesen wurde, sehen Sie genau das, was diese Rolle
              hergibt — vorher nichts. Ein erneutes Laden genügt dann.
            </p>
          ) : (
            <p style={{ maxInlineSize: '52ch' }}>
              Für den Aufruf wird das Recht <strong translate="no">{recht}</strong> benötigt.
              {anwendungsrollen === undefined || anwendungsrollen.length === 0
                ? ' Ihr Zugang führt es nicht.'
                : anwendungsrollen.length === 1
                  ? ' Ihre Anwendungsrolle ' + anwendungsrollen[0] + ' führt es nicht.'
                  : ' Ihre Anwendungsrollen ' + anwendungsrollen.join(', ') + ' führen es nicht.'}
              {' '}Wenden Sie sich an die Administration, wenn Sie den Bereich brauchen.
            </p>
          )}
          <p style={{ marginTop: 12 }}>
            <Link to="/" className="knopf knopf--haupt">Zur Startseite</Link>
          </p>
        </div>
      </section>
    </>
  );
}

/**
 * Unbekannte Adresse.
 *
 * Vorher leitete die Auffangroute stillschweigend auf die Startseite um. Wer
 * sich vertippt oder einem veralteten Lesezeichen folgt, stand dann ohne
 * Erklaerung woanders und hielt das fuer einen Fehler der Anwendung. Besser
 * ist es zu sagen, was nicht gefunden wurde — und die Adresse zu zeigen,
 * damit der Tippfehler sichtbar wird.
 */
function NichtGefunden() {
  const ort = useLocation();
  return (
    /* Kein eigener Seitenkopf: `kopfFuerRoute` liefert fuer unbekannte Routen
       bereits die Ueberschrift „Nicht gefunden". Ein zweites `h1` daneben
       zerbricht die Gliederung, an der sich Vorleseprogramme orientieren. */
    <>
      <section className="karte">
        <div className="leer">
          <p style={{ maxInlineSize: '52ch' }}>
            Aufgerufen wurde{' '}
            <code translate="no" style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              background: 'var(--surface-sunken)', padding: '2px 6px',
              borderRadius: 'var(--r-sm)', overflowWrap: 'anywhere',
            }}>{ort.pathname}</code>. Möglicherweise ist die Adresse veraltet
            oder enthält einen Tippfehler.
          </p>
          <p style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/" className="knopf knopf--haupt">Zur Startseite</Link>
            <Link to="/sitemap" className="knopf knopf--rand">Alle Bereiche anzeigen</Link>
          </p>
        </div>
      </section>
    </>
  );
}

/** Leitet `/organisation/<code>` auf die Ansicht mit `?oe=<code>` um. */
function OrganisationWeiche() {
  const { code } = useParams();
  return <Navigate to={'/organisation?oe=' + encodeURIComponent(code ?? '')} replace />;
}

/* ------------------------------------------------------------- Cockpit */

/* ------------------------------------------------------------ Anmeldung */

/**
 * Der Name der Instanz fuer die Anmeldekarte.
 *
 * Vor der Anmeldung ist noch kein Konto bekannt, wohl aber der Mandant: er
 * wird beim Bau eingebacken (`VITE_MANDANT`). Fehlt er — die Entwicklung
 * laesst den Mandanten waehlen —, traegt die Karte die Aufgabe statt eines
 * Hauses, das noch niemand gewaehlt hat.
 */
function instanzName(): string {
  if (INSTANZ_MANDANT === null) return 'IAM';
  return PROFILE[INSTANZ_MANDANT]?.name ?? 'IAM';
}

function Anmeldeschirm() {
  return (
    <main style={{ display: 'grid', placeItems: 'center', minBlockSize: '100dvh', padding: 'var(--raum-5)' }}>
      <div className="karte bewegt-auftauchen"
           style={{ maxInlineSize: 380, inlineSize: '100%', padding: 'var(--raum-6)', textAlign: 'center' }}>
        {/* Der Name des Hauses, dann die Aufgabe — dieselbe Zerlegung wie im
            Browserreiter („HOWOGE - IAM", siehe `entwurf/reiter.ts`).
            Bis zum 01.09.2026 stand hier das fruehere Kuerzel des Produkts
            und darunter „Standardrollenmatrix": unser Name und der einer
            Tabelle, aus der der Bestand einmal kam. Beim Kunden ist beides
            die falsche Auskunft, und er hat es gemeldet. Der Mandantenname
            kommt aus `PROFILE` und nicht aus einer zweiten festen Zeile. */}
        <div className="marke" style={{ justifyContent: 'center', marginBlockEnd: 'var(--raum-4)' }}>
          <span className="marke__zeichen">IAM</span><span>{instanzName()}</span>
        </div>
        <h1 style={{ marginBlockEnd: 'var(--raum-2)' }}>IAM</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Rollen, Assets, technische Gruppen und Berechtigungszuordnungen.
          Die Anmeldung erfolgt mit Ihrem Geschäftskonto.
        </p>
        <button type="button" className="knopf knopf--haupt"
                style={{ inlineSize: '100%', blockSize: 40 }}
                onClick={() => void msal.loginRedirect({ scopes: [bereich] })}>
          Anmelden
        </button>
      </div>
    </main>
  );
}

/* ---------------------------------------------------------------- Hülle */

const MANDANT_SCHLUESSEL = 'iammatrix.marke';

/** Steht in der Fusszeile der Navigation — welcher Stand hier laeuft. */
const VERSION = 'v26.08.3';

export function App() {
  const echteAnmeldung = useIsAuthenticated();
  const angemeldet = echteAnmeldung || OHNE_ANMELDUNG;
  const ort = useLocation();
  const [navOffen, setNavOffen] = useState(false);
  /* Breite der Seitenleiste — hier statt in der Leiste selbst, damit das
     Hamburgersymbol der Kopfzeile sie erreicht. Siehe `SeitenleisteProps`. */
  const [sidebar, setzeSidebar] = useGemerkt<string>('sidebar', 'breit');
  const [thema, setThema] = useState(() => document.documentElement.dataset['theme'] ?? 'light');
  // Drei Faelle, in dieser Reihenfolge:
  //
  //   1. Die Instanz hat einen festen Mandanten (`VITE_MANDANT`). Dann gilt
  //      er, und zwar **vor** dem Browserspeicher: ein dort liegengebliebenes
  //      `comline` aus einer anderen Bereitstellung wuerde sonst eine Marke
  //      waehlen, die diese Instanz gar nicht fuehrt — die Oberflaeche
  //      erschiene in fremder Farbe und jede Abfrage liefe in eine 400.
  //   2. Eine getroffene Wahl aus dem Browserspeicher.
  //   3. Ohne Anmeldung DEV, sonst COMLINE.
  const [markeSchluessel, setMarkeSchluessel] = useState(
    () => INSTANZ_MANDANT
      ?? localStorage.getItem(MANDANT_SCHLUESSEL)
      ?? (OHNE_ANMELDUNG ? DEV_MANDANT : 'comline'),
  );
  /* Die Persona des Demobaus.

     Sie steuert dort, welches **Konto** die Datenquelle waehlt; die Rechte
     kommen danach wie ueberall aus `/api/ich`. In der Anwendung wird sie
     nicht gelesen — der Umschalter im Konto-Menue erscheint nur unter
     `DEMO`.

     Bis zum 27.08.2026 hing an ihr mehr: die Auskunft auf `/profil`, die
     Sperren im Wegweiser, die Kacheln der Startseite und die Zaehler. Alles
     davon las eine Tabelle im Quelltext, nachgeschlagen ueber einen Wert im
     **Browserspeicher**. */
    const [persona, setPersona] = useGemerkt<string>('persona', 'IAM Admin');

  const profil: Profil = PROFILE[markeSchluessel] ?? PROFILE['comline']!;
  const mandant = profil.name;

  /* Den Zuschnitt eine Ebene nach oben melden — der Drawer haengt ueber
     dieser Komponente und bekommt ihn sonst nicht. Siehe `Modulrahmen.tsx`. */
  useModuleMelden(profil.module);

  const { daten: uebersicht } = useUebersicht(angemeldet ? mandant : '');
  const navZaehler = useNavigationszaehler(angemeldet ? mandant : '');
  const ichAbruf = useIch(angemeldet ? mandant : '');
  const ich = ichAbruf.daten;

  // Der Umschalter wechselt Erscheinungsbild und Funktionsumfang, sofort und
  // ohne Neuladen — wie in der Vorlage.
  useEffect(() => {
    // Das Merkmal traegt jetzt jeden Mandanten, nicht nur HOWOGE. COMLINE
    // bleibt der Grundfall der Tokens; DEV braucht es, damit die
    // Umgebungskennzeichnung eine eigene Farbe bekommt und nicht in
    // COMLINE-Orange erscheint — was genau das verdecken wuerde, was sie
    // anzeigen soll.
    document.documentElement.dataset['marke'] = markeSchluessel;
    localStorage.setItem(MANDANT_SCHLUESSEL, markeSchluessel);

    /* Reiter: Name des Mandanten und Symbol in seiner Farbe. Begründung in
       `entwurf/reiter.ts`. */
    reiterSetzen(profil.name, profil.primaer);
  }, [markeSchluessel, profil.name, profil.primaer]);

  useEffect(() => {
    document.documentElement.dataset['theme'] = thema;
    localStorage.setItem('iammatrix.thema', thema);
  }, [thema]);

  useEffect(() => { setNavOffen(false); }, [ort.pathname]);

  // Titel, Untertitel, Aktionen und Brotkruemel der aktuellen Route
  const kopfRoh = useMemo(() => kopfFuerRoute(ort.pathname, ort.search), [ort.pathname, ort.search]);

/* `const rollen = useMemo(() => [persona], [persona])` stand hier und hiess
   „Anwendungsrollen des Angemeldeten". Es war eine Liste mit genau einem
   Element, und dieses Element kam aus dem Browserspeicher. Startseite und
   Zaehler pruefen ihre Rechte jetzt ueber `darfDas` — dieselbe Funktion,
   die die Routensperre unten benutzt. */

  /**
   * Darf der Angemeldete das?
   *
   * Genau eine Quelle: `ich.rechte` aus der Schnittstelle. Sie kommt aus
   * `iam.Kontorolle` und `iam.Rollenrecht` und laesst sich vom Browser nicht
   * aendern.
   *
   * Hier stand ein Rueckfall auf die im Konto-Menue gewaehlte **Persona**,
   * begruendet mit der getrennten Auslieferung von Oberflaeche und
   * Schnittstelle. Die Begruendung galt, solange die Schnittstelle keine
   * Rechte mitgab; seit dem Berechtigungsmodell tut sie es. Der Rueckfall war
   * damit nur noch eines: eine Rechteentscheidung, die bei jedem Ausfall von
   * `/api/ich` stillschweigend auf einen Wert kippt, den der Benutzer selbst
   * im Menue setzt. Genau umgekehrt, als eine Sperre sich verhalten muss.
   *
   * Solange die Rechte nicht bekannt sind, lautet die Antwort **nein**. Das
   * ist die Grundeinstellung des Modells — was nicht vergeben ist, ist
   * gesperrt. Damit daraus kein falsches „Keine Berechtigung" waehrend des
   * Ladens wird, unterscheidet die Ansicht unten „laedt noch" von „darf
   * nicht"; die Sperre selbst bleibt geschlossen.
   *
   * Zu tragen hat sie ohnehin nur die Anzeige — die Pruefung, auf die es
   * ankommt, sitzt in `apps/api/src/berechtigung.ts`.
   */
  const rechteBekannt = Array.isArray(ich?.rechte);
  /* Der Rueckblick. `App` zeichnet das Band; gesetzt wird der Stichtag in der
     Kopfzeile, gelesen wird er ausserdem von jedem Abruf. */
  const stichtag = useStand();
  const stichtagSetzen = useStandSetzen();

  const darfDas = useCallback(
    (recht: string | null) => (Array.isArray(ich?.rechte) ? hatRecht(ich.rechte, recht) : false),
    [ich?.rechte],
  );

  /**
   * Der Seitenkopf ohne die Aktionen, die dem Angemeldeten fehlen.
   *
   * Ein Knopf, der nichts tun darf, **verschwindet** — er wird nicht
   * ausgegraut. Das ist die Regel seit dem 13.08.2026, und sie gilt hier
   * doppelt: „Bearbeiten" am Rollendetail fuehrte eine Fuehrungskraft ohne
   * `rollen.schreiben` in den Editor, wo sie ein vollstaendiges Formular
   * ausfuellen konnte und beim Speichern eine 403 bekam. Gemeldet am
   * 28.08.2026.
   *
   * Eine Aktion **ohne** Recht bleibt stehen: die meisten sind Wege, keine
   * Eingriffe.
   */
  const kopf = useMemo(() => (kopfRoh === null ? null : {
    ...kopfRoh,
    aktionen: kopfRoh.aktionen.filter((a) => !a[4] || darfDas(a[4])),
  }), [kopfRoh, darfDas]);

  /* Konten des Entwicklungsbetriebs.

     Nur dort: die ausgelieferte Anwendung kennt kein Kontowaehlen, dort
     entscheidet das Token. `aus` schaltet die Anfrage ganz ab, damit sie in
     der Produktion nicht als 404 gegen einen Endpunkt laeuft, den es dort
     (noch) nicht gibt. */
  const konten = useListe<{ kennung: string; name: string; anwendungsrolle: string; oeCode: string }>(
    'konten', mandant,
    { seite: 1, groesse: 200, sortierung: 'name', richtung: 'auf', aus: !import.meta.env.DEV },
  );
  const [gewaehltesKonto, setzeGewaehltesKonto] = useGemerkt<string>('konto', '');

  /* Was der Mandant **fuehrt**, und was der Angemeldete **darf**, sind zwei
   * verschiedene Fragen — und sie gehoeren verschieden beantwortet.
   *
   * Ein Modul, das dieser Mandant nicht fuehrt, gibt es fuer ihn nicht: ohne
   * Personenbezug ist `/personen` keine gesperrte Seite, sondern keine Seite.
   * Das bleibt gefiltert.
   *
   * Ein Eintrag dagegen, fuer den nur das **Recht** fehlt, wurde bis zum
   * 14.08.2026 ebenfalls entfernt. Die Navigation sah dann fuer jeden anders
   * aus, und niemand konnte sehen, dass es den Bereich ueberhaupt gibt —
   * geschweige denn, welches Recht ihm fehlt. Jetzt steht er da, mit
   * Vorhaengeschloss und dem Grund im Tooltip, und ist **nicht** anklickbar.
   *
   * Bis zum 20.08.2026 fiel die Gruppe „System" ganz weg, wenn jemand die
   * Entra-Rolle „Verwaltung" nicht trug. Das ist entfallen: welche Bereiche es
   * gibt, ist keine Frage der Anmeldung, sondern eine des Rechtekatalogs — und
   * der sperrt einzeln, mit Vorhaengeschloss und Grund. */
  const gruppen = NAVIGATION
    .map((g) => ({
      ...g,
      eintraege: g.eintraege.filter((eintrag) => routeErlaubt(eintrag.route, profil)),
    }))
    .filter((g) => g.eintraege.length > 0);

  /* Die Suchpalette bietet nur an, was auch benutzbar ist. Ein Treffer, der
     beim Anklicken in „keine Berechtigung" endet, ist kein Treffer. */
  const alleEintraege = gruppen
    .flatMap((g) => g.eintraege)
    .filter((eintrag) => darfDas(eintrag.recht));

  if (!angemeldet) return <Anmeldeschirm />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-page)', display: 'flex', flexDirection: 'column' }}>
      <a className="sprung" href="#inhalt">Zum Inhalt springen</a>

      <Kopfzeile
        marke={markeSchluessel}
        setzeMarke={setMarkeSchluessel}
        thema={thema}
        setzeThema={setThema}
        persona={persona}
        /* In der Demo haengen die Rechte an einem Konto, das die Datenquelle
           aus der Persona ableitet — dieselbe Mechanik wie der Kontowechsel
           des Entwicklungsbetriebs, und wie dort greift sie erst mit dem
           Neuladen. */
        setzePersona={DEMO
          ? (p) => { setPersona(p); window.location.reload(); }
          : setPersona}
        konten={konten.daten?.zeilen ?? []}
        gewaehltesKonto={gewaehltesKonto}
        setzeGewaehltesKonto={(k) => { setzeGewaehltesKonto(k); window.location.reload(); }}
        profil={profil}
        mandant={mandant}
        darfDas={darfDas}
        /* Ein Symbol, zwei Fensterbreiten.
           Unter 1024 px liegt die Navigation als Ueberlagerung ueber dem
           Inhalt — dort oeffnet und schliesst der Knopf sie. Ab 1024 px steht
           sie fest daneben; dann klappt er sie schmal und wieder breit. Die
           Grenze ist dieselbe wie in `huelle.css`; eine zweite Zahl waere ein
           zweiter Ort zum Vergessen. */
        onNavigation={() => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            setzeSidebar(sidebar === 'breit' ? 'rail' : 'breit');
          } else {
            setNavOffen((o) => !o);
          }
        }}
        name={ich?.name}
        upn={ich?.kennung}
        /* Ab wann es einen Verlauf gibt — die Stichtagswahl bietet keine
           frueheren Tage an, sonst endete jeder Griff in einer Absage. */
        verlaufAb={ich?.verlaufAb ?? null}
      />

      {/* Das Band des Rückblicks.
       *
       * Es steht zwischen Kopfzeile und Inhalt und damit über Navigation
       * **und** Ansicht: einen Routenwechsel überdauert es, und es lässt sich
       * nicht wegscrollen. Das ist Absicht — die teuerste Verwechslung dieser
       * Funktion wäre, einen alten Stand für den heutigen zu halten.
       *
       * Die Farbe ist die der Warnung und nicht die des Fehlers: hier ist
       * nichts kaputt, es ist nur nicht jetzt. */}
      {stichtag && (
        <div role="status" style={stil(
          'display:flex;align-items:center;gap:12px;flex-wrap:wrap;'
          + 'padding:8px 16px;background:var(--warning-subtle);'
          + 'border-block-end:1px solid var(--warning);color:var(--warning);'
          + 'font-size:13px;line-height:18px',
        )}>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
               style={stil('flex:0 0 16px')}>
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2" />
          </svg>
          <span>
            <strong style={stil('font-weight:600')}>
              Ansicht vom {datum(stichtag.tag)}
            </strong>
            {' — nur lesen. Geändert wird nichts, und Ansichten ohne Verlauf sagen es.'}
          </span>
          <button onClick={() => stichtagSetzen(null)} style={stil(
            'margin-inline-start:auto;height:32px;padding:0 12px;cursor:pointer;'
            + 'background:var(--warning);color:var(--text-on-brand);border:none;'
            + 'border-radius:var(--r-md);font-size:13px;font-weight:600',
          )}>
            Zurück zu heute
          </button>
        </div>
      )}

      {navOffen && (
        <div className="nav__schleier" onClick={() => setNavOffen(false)} aria-hidden="true" />
      )}

      {/* Navigation und Inhalt stehen nebeneinander, wie in der Vorlage. */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        <Seitenleiste
          profil={profil}
          darfDas={darfDas}
          offen={navOffen}
          onSchliessen={() => setNavOffen(false)}
          version={VERSION}
          zaehler={navZaehler}
          sidebar={sidebar}
          setzeSidebar={setzeSidebar}
        />

        <main id="inhalt" tabIndex={-1}
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}>
          {/* Der Seitenkopf traegt Titel und Aktionsschaltflaechen der Ansicht.
              Er faellt bei beiden Sperren mit — sonst kuendigt er eine Ansicht
              an, die darunter gar nicht erscheint, und bietet Schaltflaechen zu
              etwas, das man nicht sehen darf. */}
          {kopf && routeErlaubt(ort.pathname, profil)
            && darfDas(rechtFuerRoute(ort.pathname))
            && <Seitenkopf kopf={kopf} pfad={ort.pathname} />}
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {/* Keine gemeinsame Obergrenze.
                Lesende Ansichten — Startseite, Cockpit — begrenzen und
                zentrieren sich selbst; dort ist eine ruhige Spaltenbreite
                der Zweck. Datenansichten sollen dagegen die volle Breite bis
                zur Navigation nutzen: jede Spalte, die nicht auf den Schirm
                passt, kostet einen Blattwechsel. Eine Obergrenze an dieser
                Stelle wuerde beiden dasselbe aufzwingen. */}
            <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
        {/* Zwei Sperren, in dieser Reihenfolge.

            Zuerst der Mandantenzuschnitt: was ein Mandant nicht führt, gibt es
            für niemanden — auch nicht für die Administration. Danach das eigene
            Recht.

            Die zweite Sperre fehlte bis zum 11.08.2026 ganz. `darf(...)` filterte
            nur die Navigationsliste; wer die Adresse eintippte, kam hinein. Eine
            Rechtevergabe, die sich mit der Adresszeile umgehen lässt, ist keine. */}
        {!routeErlaubt(ort.pathname, profil) ? <NichtImProfil profil={profil} />
         /* „Noch nicht bekannt" ist nicht „nicht erlaubt". Solange `/api/ich`
            laeuft, steht hier ein Ladezustand — sonst blitzt bei jedem
            Seitenaufruf „Keine Berechtigung" auf, ehe die Rechte da sind, und
            man haelt eine Ladezeit fuer eine Sperre. Gesperrt bleibt es
            trotzdem: `darfDas` sagt in dieser Zeit nein. */
         : !rechteBekannt && ichAbruf.laedt
           ? <div style={{ flex: 1, padding: '24px' }}><Laedt zeilen={5} hoehe={44} /></div>
         : !darfDas(rechtFuerRoute(ort.pathname))
           ? <KeinRecht recht={rechtFuerRoute(ort.pathname) ?? ''}
                       anwendungsrollen={ich?.anwendungsrollen}
                       ohneRolle={rechteBekannt && (ich?.rechte?.length ?? 0) === 0} /> : (
        /* Die Grenze sitzt hier und nicht um die ganze Anwendung: Kopfzeile und
           Navigation sollen stehen bleiben, wenn eine Ansicht abbricht. Ohne
           sie nahm React bei jedem Renderfehler den gesamten Baum vom
           Bildschirm — eine weiße Seite ohne Meldung, aus der weder hervorging,
           was fehlte, noch wie man wieder herauskommt. */
        <Fehlergrenze zuruecksetzen={ort.pathname} version={VERSION}>
        {/* Der Platzhalter beim ersten Aufruf einer Seite. Derselbe Baustein
            wie bei jedem anderen Ladevorgang — ein eigener Ladezustand nur
            fuer das Nachladen von Programmteilen waere eine zweite Sprache
            fuer dieselbe Sache. */}
        <Suspense fallback={
          <div style={{ flex: 1, padding: '24px' }}><Laedt zeilen={5} hoehe={44} /></div>
        }>
        <Routes>
          <Route path="/" element={
            <Startseite vorname={(ich?.name ?? "").split(" ")[0] ?? "Kollege"} profil={profil}
                        darfDas={darfDas} mandant={mandant} />
          } />
          <Route path="/cockpit" element={<CockpitSeite profil={profil} mandant={mandant} uebersicht={uebersicht} />} />
          <Route path="/rollen" element={
            <Rollenkatalog mandant={mandant}
                           personenbezug={profil.module.personenbezug !== false}
                           kritikalitaet={profil.module.kritikalitaet !== false}
                           rezert={profil.module.rezert !== false} />
          } />
          <Route path="/rollen/bearbeiten"
                 element={<Rolleneditor mandant={mandant} darfDas={darfDas} profil={profil} />} />
          <Route path="/rollen/:rolleId/bearbeiten"
                 element={<Rolleneditor mandant={mandant} darfDas={darfDas} profil={profil} />} />
          <Route path="/rollen/:rolleId" element={
            <Rollendetail mandant={mandant} personenbezug={profil.module.personenbezug !== false}
                          sod={profil.module.sod !== false} rezert={profil.module.rezert !== false}
                          kritikalitaet={profil.module.kritikalitaet !== false}
                          rollenfristen={profil.module.rollenfristen !== false}
                          gruppenmapping={profil.module.gruppenmapping !== false} />
          } />
          <Route path="/matrix" element={<Rollenmatrix mandant={mandant} module={profil.module} />} />
          <Route path="/rechte" element={<Berechtigungsfeld mandant={mandant} />} />
          <Route path="/konten" element={<Konten mandant={mandant} />} />
          <Route path="/heatmap" element={<Heatmap mandant={mandant} />} />
          <Route path="/graph" element={
            <Beziehungsgraph mandant={mandant} personenbezug={profil.module.personenbezug !== false} />
          } />
          <Route path="/assets/lizenzen" element={<Lizenzen mandant={mandant} />} />
          <Route path="/personen" element={<Personen mandant={mandant} />} />
          <Route path="/personen/:personId" element={<Personendetail mandant={mandant} />} />
          <Route path="/vertretungen" element={<Vertretungen mandant={mandant} />} />
          <Route path="/antraege/neu" element={
            <Antragsassistent mandant={mandant} personenbezug={profil.module.personenbezug !== false} />
          } />
          <Route path="/antraege/meine" element={<MeineAntraege mandant={mandant} />} />
          <Route path="/genehmigungen" element={<Genehmigungen mandant={mandant} />} />
          <Route path="/lifecycle" element={
            <Lebenszyklus mandant={mandant} personenbezug={profil.module.personenbezug !== false} />
          } />
          <Route path="/abfragen" element={<Abfragen mandant={mandant} profil={profil} />} />
          <Route path="/ausfuehrung" element={<Ausfuehrung mandant={mandant} />} />
          {['/aktive-nutzer', '/audit', '/sitemap', '/dev/issues', '/profil'].map((pfad) => (
            <Route key={pfad} path={pfad} element={
              <Querschnitt mandant={mandant} profil={profil} />
            } />
          ))}
          {['/rezertifizierung', '/sod', '/richtlinien', '/offene-punkte', '/reports'].map((pfad) => (
            <Route key={pfad} path={pfad} element={<Governance mandant={mandant} profil={profil} />} />
          ))}
          {Object.keys(ASSET_KONFIG).map((pfad) => (
            <Route key={pfad} path={pfad} element={
              <Assets mandant={mandant} personenbezug={profil.module.personenbezug !== false}
                      module={profil.module} />
            } />
          ))}
          <Route path="/organisation" element={
            <Organisation mandant={mandant} personenbezug={profil.module.personenbezug !== false}
                          darfDas={darfDas} />
          } />
          {/* Die Ansicht waehlt die Einheit ueber `?oe=`, der Kopfbereich kennt
              aber auch `/organisation/<code>`. Ohne diese Weiche landet eine
              getippte oder gemerkte Adresse stillschweigend auf der
              Startseite. */}
          <Route path="/organisation/:code" element={<OrganisationWeiche />} />
          {alleEintraege
            .filter((eintrag) => !NACHGEBAUT.has(eintrag.route))
            .map((eintrag) => {
              const anbindung = ANBINDUNG[eintrag.route];
              /* Ohne Anbindung gibt es keine Route — und damit greift
                 `NichtGefunden` unten, das den Weg zurueck zeigt. Bis zum
                 27.08.2026 stand hier `NochNicht`, das stattdessen sagte,
                 die Ansicht sei „noch nicht nachgebaut". Der Pruefer
                 `routenlage` sorgt dafuer, dass der Fall gar nicht
                 eintritt. */
              if (!anbindung) return null;
              return (
                <Route key={eintrag.route} path={eintrag.route}
                       element={<Liste eintrag={eintrag} anbindung={anbindung} mandant={mandant} />} />
              );
            })}
          <Route path="*" element={<NichtGefunden />} />
        </Routes>
        </Suspense>
        </Fehlergrenze>
        )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
